import { memo, type ReactNode } from 'react';

import type { Action } from '../../../types/Action';
import type { SharedIconProps } from '../../../types/SharedIconProps';
import type { SharedStatusBadgeProps } from '../../../types/SharedStatusBadgeProps';

import { Actions } from '../../Actions/Actions';
import { Layout } from '../../Layout/Layout';
import { StatusBadge } from '../../StatusBadge/StatusBadge';
import './PageHeader.css';
import { Text } from '../../Text/Text';

interface Props {
  title: ReactNode;
  actions?: Action[];
  leadingSlot?: ReactNode;
  moreActions?: {
    actions: Action[];
    icon: SharedIconProps;
  };
  statusBadge?: SharedStatusBadgeProps;
  subtitle?: ReactNode;
  titleSlot?: ReactNode;
  trailingSlot?: ReactNode;
}

export const PageHeader = memo(
  ({
    title,
    actions,
    leadingSlot,
    moreActions,
    statusBadge,
    subtitle,
    titleSlot,
    trailingSlot,
  }: Props) => {
    const hasActions = Boolean(actions || moreActions);
    return (
      <header className="eds-page-header-host">
        <div className="eds-page-header">
          <Layout
            className="eds-page-header--title-block"
            direction="column"
            spacing="xs"
          >
            <Layout align="center" spacing="xs">
              {leadingSlot && (
                <Layout className="eds-page-header--title-adornment">
                  {leadingSlot}
                </Layout>
              )}
              <Layout className="eds-page-header--title">
                <Text as="h1" truncate={1} variant="heading-m">
                  {title}
                </Text>
              </Layout>
              {statusBadge && (
                <Layout className="eds-page-header--title-adornment">
                  <StatusBadge {...statusBadge} />
                </Layout>
              )}
              {titleSlot && (
                <Layout className="eds-page-header--title-adornment">
                  {titleSlot}
                </Layout>
              )}
            </Layout>
            <Subtitle subtitle={subtitle} />
          </Layout>
          {Boolean(trailingSlot || hasActions) && (
            <Layout align="center" spacing="s">
              {trailingSlot}
              {hasActions && (
                <Actions actions={actions ?? []} moreActions={moreActions} />
              )}
            </Layout>
          )}
        </div>
      </header>
    );
  },
);

PageHeader.displayName = 'PageHeader';

function Subtitle({ subtitle }: { subtitle?: ReactNode }) {
  if (!subtitle) return null;
  if (typeof subtitle === 'string') {
    return (
      <Text as="p" tone="subdued" variant="body-m">
        {subtitle}
      </Text>
    );
  }
  return subtitle;
}
